import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCrewLabelTable } from "../../shared/crew/useCrewLabels";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { isApiProblem, problemMessage } from "../../shared/api/problem";
import { Dialog } from "../../shared/components/Dialog";
import { featuresQueryKey, fetchFeatures, fetchMe, meQueryKey } from "../../shared/session/queries";
import { authText } from "../../shared/i18n/authText";
import type { Me } from "../../shared/api/types";
import { SessionContext, buildSession, mustChangePassword, type Session } from "../../shared/session/session-context";
import { SessionExpiredContext } from "../../shared/session/expired-context";
import { ChangePasswordPage } from "./ChangePasswordPage";
import { LoginForm } from "./LoginForm";
import { PasskeySignInOption } from "./PasskeySignInOption";
import { usePasskeySignIn } from "./passkeySignIn";
import { expiredSessionNavigation, onSessionExpired } from "./sessionEvents";
import { portalTarget } from "./api";

function StartupError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <main className="gf-login" id="main">
      <div className="gf-login-card stack">
        <h1>Giraffe could not start</h1>
        <div className="msg" role="alert">
          {problemMessage(error)}
        </div>
        <button type="button" className="btn" onClick={onRetry}>
          Retry
        </button>
      </div>
    </main>
  );
}

/** The person who signed in (the admin themselves while test mode views as someone else). */
function signedInPersonId(me: Me): number {
  return me.testMode?.realUser.id ?? me.user.id;
}

/**
 * A 401 while signed in: sign in again over the page, keeping what was typed. Batch 21 GRF-70: with the Passkeys flag on and a
 * browser that can do WebAuthn, the dialog offers "Sign in with a passkey" like the login page (the same hook and block; the
 * e-mail autofill offers the passkey too), and nothing passkey-related runs while the dialog is closed. When the new sign-in
 * is a DIFFERENT person than the one whose session expired (by password or passkey), the page is not kept: it reloads to
 * Home, so nothing of the first person's page, drafts or cached data stays on screen for the second.
 */
function SessionExpiredDialog({ open, session, onContinue }: { open: boolean; session: Session; onContinue: () => void }) {
  const queryClient = useQueryClient();
  const locale = session.locale;
  const before = signedInPersonId(session.me);
  const signedIn = useCallback(async () => {
    // Read straight from the server, not into the cache: another person's Me must never reach this page's session.
    const now = await fetchMe();
    if (signedInPersonId(now) !== before) {
      expiredSessionNavigation.reloadHome();
      return;
    }
    onContinue();
    await queryClient.invalidateQueries();
  }, [before, onContinue, queryClient]);
  const passkey = usePasskeySignIn({ enabled: open && session.flag("Passkeys"), locale, onSignedIn: signedIn });

  return (
    <Dialog open={open} title={authText("expiredTitle", locale)} onClose={() => undefined} dismissible={false}>
      <p>{authText("expiredBody", locale)}</p>
      <LoginForm
        submitLabel={authText("expiredSubmit", locale)}
        onSignedIn={signedIn}
        passkeyAutofill={passkey.available}
        onPasswordStart={passkey.cancelWaiting}
        onPasswordFailed={passkey.resumeWaiting}
      />
      <PasskeySignInOption passkey={passkey} locale={locale} />
    </Dialog>
  );
}

/**
 * Loads the principal and flags once. Without a session it sends the visitor to sign-in with `next` = the exact
 * path and query they asked for (UX-01 Rev 3.2); a 401 later re-authenticates over the page.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const me = useQuery({ queryKey: meQueryKey, queryFn: fetchMe, retry: false, staleTime: Infinity });
  // While the password must be changed the server refuses every call except /auth/* and GET /features (SA Rev 29).
  const changeRequired = me.data ? mustChangePassword(me.data) : false;
  // Batch 19 S8d: an external tester reaches only the sign-in endpoints and the portal API, so nothing else is asked for them.
  const external = me.data?.user.userKind === "external";
  const features = useQuery({ queryKey: featuresQueryKey, queryFn: fetchFeatures, enabled: me.isSuccess && !external, staleTime: 60_000 });
  // Crew roles (Revision 104): the label table is read with the session and waited for, so every screen and every pure text
  // builder shows the server's words from the first render. A failure is not fatal: roles then read as their keys.
  const crew = useCrewLabelTable({ enabled: me.isSuccess && !changeRequired && !external });
  const [expired, setExpired] = useState(false);
  // UX-01 §4.15: after a forced change, confirm it once on the page the user returns to.
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => onSessionExpired(() => setExpired(true)), []);

  const session = useMemo(() => (me.data && features.data ? buildSession(me.data, features.data) : null), [me.data, features.data]);

  if (me.isError) {
    if (isApiProblem(me.error) && me.error.isUnauthorized) {
      return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    }
    return <StartupError error={me.error} onRetry={() => void me.refetch()} />;
  }
  // Batch 19 S8d (SA-B19-EVENTS §4.1, UX §1.1, XP-AC3): an external tester never enters the internal shell. Before anything of it
  // paints (no flags, no crew labels, no side nav) they go to the portal; a link to an item goes to that item in the portal.
  if (me.data && external) return <Navigate to={portalTarget(location.pathname)} replace />;
  // Auth ruling (16 Sep): every route shows the change-password screen until it succeeds; the URL is kept, so the
  // refetched Me returns the user to the page they asked for.
  if (me.data && changeRequired) {
    return <ChangePasswordPage locale={me.data.user.locale} email={me.data.user.email ?? ""} onChanged={() => setPasswordChanged(true)} />;
  }
  if (features.isError) return <StartupError error={features.error} onRetry={() => void features.refetch()} />;
  // `isFetched` counts a failure too, and stays true while a later refetch runs, so the gate never flips back to loading.
  if (!session || !crew.isFetched) {
    return (
      <div className="gf-startup" role="status">
        Loading Giraffe…
      </div>
    );
  }

  return (
    <SessionContext value={session}>
      {passwordChanged && (
        <p className="msg info gf-banner" role="status">
          {authText("changeDone", session.locale)}
        </p>
      )}
      {/* What's new (ruling of 17 Oct 2026) never opens over the session-expired dialog: it reads this and waits. */}
      <SessionExpiredContext value={expired}>{children}</SessionExpiredContext>
      <SessionExpiredDialog open={expired} session={session} onContinue={() => setExpired(false)} />
    </SessionContext>
  );
}
